import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { APP_CONFIG } from '../../app.config';
import { PaymentMethod } from '../../shared/enums/payment-method.enum';
import { StoreRegisterType } from '../../shared/enums/store-register-type.enum';
import { UtilHelper } from '../../shared/helpers/utils.helper';
import { SubscriptionCoupon, CouponValidate } from '../../shared/models/subscription-coupon.model';
import {
  StorePaymentMethod,
  StorePaymentMethodsOptions,
} from '../../shared/models/payment-method.model';
import { Pendency } from '../../shared/models/pendency.model';
import { Plan } from '../../shared/models/plan.model';
import { StoreOrigin } from '../../shared/models/store-origin.model';
import { ShortLink } from '../../shared/models/shortlink.model';
import { RequestService } from '../../shared/services/request.service';
import { BusinessHour } from './models/business-hour.model';
import { Store, StoreAddressForm, StoreDataForm } from './models/store.model';

@Injectable()
export class StoreService {
  constructor(
    private decimalPipe: DecimalPipe,
    private requestService: RequestService
  ) {}

  async getZipCode(zipCode: string): Promise<any> {
    return await this.requestService.getData('https://viacep.com.br/ws/' + zipCode + '/json/');
  }

  async validateCnpj(cnpj: string): Promise<void> {
    const cnpjUnmasked: string = UtilHelper.unmask(cnpj);
    return await this.requestService.getData(APP_CONFIG.WEBSERVICE.VALIDATE_CNPJ + cnpjUnmasked);
  }

  async validateEmail(email: string): Promise<void> {
    return await this.requestService.getData(APP_CONFIG.WEBSERVICE.VALIDATE_EMAIL + email);
  }

  async sendEmailVerificationCode(name: string, email: string): Promise<void> {
    const data: any = {
      name: name,
      email: email,
    };
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.SEND_EMAIL_CODE, data);
  }

  async getData(estabId: number): Promise<Store> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { estabId: estabId },
      APP_CONFIG.WEBSERVICE.GET_ESTABLISHMENT_DATA
    );
    const response: Store = await this.requestService.getData(urlRequest);
    console.log(response);
    return new Store(response);
  }

  async getPaymentMethods(storeId: number): Promise<StorePaymentMethodsOptions> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.PAYMENT_METHODS
    );
    const response: StorePaymentMethodsOptions = await this.requestService.getData(urlRequest);
    const methods = new StorePaymentMethodsOptions();
    if (response) {
      (response.online || []).forEach((item) => {
        methods.online.push(new StorePaymentMethod(item));
      });
      (response.local || []).forEach((item) => {
        methods.local.push(new StorePaymentMethod(item));
      });
    }
    return methods;
  }

  getPaymentMethodsInCache(storeId: number): StorePaymentMethodsOptions {
    let today: Date = new Date();
    let paymentsInCacheString = localStorage.getItem('storePaymentMethodsOptions-' + storeId);
    if (paymentsInCacheString) {
      let paymentsInCache: { methods: StorePaymentMethodsOptions; date: number } =
        JSON.parse(paymentsInCacheString);
      let oneDayTime = 3600 * 1000 * 24;
      if (paymentsInCache.date + oneDayTime > today.getTime()) {
        return new StorePaymentMethodsOptions(paymentsInCache.methods);
      }
    }
    return null;
  }

  savePaymentMethodsInCache(storeId: number, payments: StorePaymentMethodsOptions): void {
    let paymentsInCache = {
      methods: payments,
      date: new Date().getTime(),
    };
    localStorage.setItem('storePaymentMethodsOptions-' + storeId, JSON.stringify(paymentsInCache));
  }

  checkToShowPixNews(paymentMethods: StorePaymentMethodsOptions): boolean {
    if (paymentMethods.online.length > 0) {
      return (
        paymentMethods.online.find(
          (p) => p.type['code'] == PaymentMethod.APP_PIX['code'] && !p.accepted
        ) != null
      );
    }
    return false;
  }

  async updatePaymentMethods(storeId: number, methods: StorePaymentMethodsOptions): Promise<void> {
    const data: any = methods.toJson();
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.PAYMENT_METHODS
    );
    return await this.requestService.putData(urlRequest, data);
  }

  async updateIdentity(estabIdentity: any): Promise<any> {
    const data: any = JSON.parse(JSON.stringify(estabIdentity));
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.UPDATE_STORE_IDENTITY, data);
  }

  async updateStoreData(storeData: StoreDataForm): Promise<any> {
    return await this.requestService.putData(
      APP_CONFIG.WEBSERVICE.UPDATE_STORE_DATA,
      storeData.toJson()
    );
  }

  async updateStoreAddress(storeId: number, storeAddress: StoreAddressForm): Promise<any> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.UPDATE_STORE_ADDRESS
    );
    return await this.requestService.putData(urlRequest, storeAddress.toJson());
  }

  async updatePendencyStore(storeId: number, pendency: Pendency): Promise<any> {
    const data: any = pendency.toUpdateJson(storeId, 'OK');
    return await this.requestService.putData(APP_CONFIG.WEBSERVICE.PENDENCIES, data);
  }

  async registerNewStore(
    store: Store,
    selectedPlan: Plan,
    password: string,
    emailValidationCode: string,
    captchaResponse: string,
    coupon?: SubscriptionCoupon,
    origin?: StoreOrigin
  ): Promise<any> {
    const urlRequest: string = APP_CONFIG.WEBSERVICE.SAVE_STORE;
    const data: any = {
      emailValidationCode: emailValidationCode,
      store: {
        tradeName: UtilHelper.removeLinksAndInvalidKeywords(store.tradeName),
        //cnpj: UtilHelper.unmask(store.cnpj),
        howKnow: store.howKnow,
        planId: selectedPlan.id,
        extraModulesId: this.getExtraModulesId(selectedPlan),
        coupon: coupon ? coupon.code : null,
      },
      user: {
        name: store.contactName,
        username: store.contactEmail,
        ddiContactMobilePhone: UtilHelper.unmask(store.ddiContactMobilePhone),
        phone: UtilHelper.unmask(store.contactMobilePhone),
        password: password,
      },
      origin: origin,
    };
    console.log(JSON.stringify(data));
    this.requestService.addParamsHeader('g-recaptcha-response', captchaResponse || '');
    return await this.requestService.postData(urlRequest, data);
  }

  async preregister(
    tradeName: string,
    contactName: string,
    phone: string,
    email: string,
    howKnow?: string
  ): Promise<any> {
    const data: any = {
      tradeName: UtilHelper.removeLinksAndInvalidKeywords(tradeName),
      contactName: contactName,
      phone: UtilHelper.unmask(phone),
      email: email,
      howKnow: howKnow || '',
      countryPhoneCode: '55',
    };
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_PRE_STORE, data);
  }

  async register(
    store: Store,
    type: StoreRegisterType,
    selectedPlan?: Plan,
    coupon?: SubscriptionCoupon
  ): Promise<any> {
    let urlRequest: string;

    switch (type) {
      case StoreRegisterType.BRANCH:
        urlRequest = APP_CONFIG.WEBSERVICE.SAVE_BRANCH;
        break;

      case StoreRegisterType.PLACE_STORE:
        urlRequest = APP_CONFIG.WEBSERVICE.SAVE_PLACE_STORE;
        break;
    }
    const data: any = store.toJsonNewStore(type);
    if (selectedPlan) {
      data.planId = selectedPlan.id;
      data.extraModulesId = this.getExtraModulesId(selectedPlan);
    }
    if (coupon) {
      data.coupon = coupon.code;
    }
    console.log(JSON.stringify(data));
    const response: any = await this.requestService.postData(urlRequest, data, true);
    return UtilHelper.getIdFromLocationPath(response.location);
  }

  async copyMenuToStore(categoriesToCopy: Array<number>, toStoreId: number): Promise<any> {
    const data: any = {
      categories: categoriesToCopy,
      toStoreId: toStoreId,
    };
    this.requestService.addParamsHeader('timeout', '60000'); // 1min
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.COPY_MENU_TO_STORE, data);
  }

  async createPaymentId(storeId: number): Promise<any> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.CREATE_STORE_PAYMENT_ID
    );
    return await this.requestService.postData(urlRequest);
  }

  private getExtraModulesId(selectedPlan: Plan): Array<number> {
    const extras: Array<number> = selectedPlan.extraModules
      .filter((m) => m.selected)
      .map((m) => m.id);
    if (extras && extras.length > 0) {
      return extras;
    }
    return null;
  }

  async validateCoupon(code: string, translateService: TranslateService): Promise<any> {
    const couponValidate: CouponValidate = await this.requestService.getData(
      APP_CONFIG.WEBSERVICE.VALIDATE_COUPON + code
    );
    console.log(couponValidate);
    if (couponValidate) {
      if (couponValidate.success) {
        const coupon: SubscriptionCoupon = new SubscriptionCoupon().toCouponFromValidate(
          couponValidate
        );
        return {
          success: true,
          coupon: coupon,
        };
      } else {
        let complementMessage: string = '';
        if (
          couponValidate.couponMessage &&
          couponValidate.couponMessage === 'COUPON.COUPON_MINIMUM_VALUE_NOT_ACHIEVED'
        ) {
          complementMessage += couponValidate.minimumValueForUse
            ? ': R$ ' + this.decimalPipe.transform(couponValidate.minimumValueForUse, '.2-2') + '.'
            : '';
        }
        return {
          success: false,
          message: translateService.instant(couponValidate.couponMessage) + complementMessage,
        };
      }
    } else {
      return {
        success: false,
        message: 'COUPON.COUPON_NOT_FOUND',
      };
    }
  }

  async checkPendencies(estabId: number): Promise<any> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { estabId: estabId },
      APP_CONFIG.WEBSERVICE.CHECK_PENDENCIES
    );
    const response: Array<Pendency> = await this.requestService.getData(urlRequest);
    const pendencies: Array<Pendency> = [];
    if (response && response.length > 0) {
      response.forEach((p: Pendency) => {
        const pendency: Pendency = new Pendency(p);
        if (pendency.pendingType) {
          pendencies.push(pendency);
        }
      });
    }
    pendencies.sort((p1, p2) => {
      return p1.pendingType['order'] > p2.pendingType['order'] ? 1 : -1;
    });
    return pendencies;
  }

  async changeStatus(estabId: number, statusCode: string): Promise<void> {
    const data: any = {
      id: estabId,
      status: statusCode,
    };
    return await this.requestService.putData(APP_CONFIG.WEBSERVICE.CHANGE_ESTAB_STATUS, data);
  }

  async openClose(storeId: number, action: 'OPEN' | 'CLOSE'): Promise<void> {
    const urlRequest: string =
      action === 'OPEN' ? APP_CONFIG.WEBSERVICE.OPEN : APP_CONFIG.WEBSERVICE.CLOSE;
    return await this.requestService.postData(urlRequest + storeId);
  }

  async saveShortLink(shortLink: ShortLink, isPlace: boolean): Promise<any> {
    const data: any = {
      destination: shortLink.destination,
      slashtag: shortLink.newSlashtag,
      isPlace: isPlace,
    };
    if (isPlace) {
      data.placeId = shortLink.placeId;
    } else {
      data.storeId = shortLink.storeId;
    }
    if (shortLink.id) {
      data.oldSlashtag = shortLink.slashtag;
      await this.requestService.putData(APP_CONFIG.WEBSERVICE.SAVE_SHORT_LINK, data);
    } else {
      await this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_SHORT_LINK, data);
    }
  }

  // checkRemainingDaysTrial(storeId: number): Promise<any> {
  // 	return new Promise((resolve, reject) => {
  // 		const urlRequest: string = this.requestService.buildUrlPathParams({ storeId: storeId }, APP_CONFIG.WEBSERVICE.CHECK_REMAINING_DAYS_TRIAL);
  // 		this.requestService.getData(urlRequest).then((response: any) => {
  // 			console.log(response);
  // 			resolve(response);
  // 		}, (error: Error) => {
  // 			console.error(error);
  // 			reject(error);
  // 		});
  // 	});
  // }

  // BusinessHours
  // ---------------------------

  async getBusinessHours(estabId: number): Promise<Array<BusinessHour>> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { estabId: estabId },
      APP_CONFIG.WEBSERVICE.GET_BUSINESS_HOURS
    );
    return await this.requestService.getData(urlRequest);
  }

  async saveBusinessHours(
    establishmentId: number,
    businessHours: Array<BusinessHour>
  ): Promise<any> {
    const businessHoursJson: Array<any> = new Array();
    businessHours.forEach((b: BusinessHour) => {
      if (b.selected) {
        businessHoursJson.push(b.toJson());
      }
    });
    const data: any = {
      businessHours: businessHoursJson,
      establishmentId: establishmentId,
    };
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_BUSINESS_HOURS, data);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/store/store.service.ts